/** @jsxImportSource @emotion/react */
import React from "react";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import {css} from "@emotion/react";
import {CustomThemeContext} from "js/common/themes/CustomThemeProvider";
import KpiByEntityPicker from "js/admin/kpis/edit-kpis/common/kpi-by-entity-picker";

const tabsStyle = (theme) => css`
    min-height: 36px;
    padding: 2px;

    .MuiTabs-indicator {
        display: none;
    }

    .MuiTabs-scrollButtons.Mui-disabled {
        opacity: 1;

        svg {
            opacity: 0.2;
        }
    }

    .MuiTabs-scrollButtons {
        position: relative;
        top: -1px;
        border-bottom: 1px solid ${theme.palette.primary.main};
    }
`;

const tabStyle = (theme) => css`
    background-color: transparent;
    border-top-left-radius: 8px;
    border-top-right-radius: 8px;
    padding: 6px 12px;
    font-size: 14px;
    text-transform: none;
    color: ${theme.palette.text.secondary};
    min-height: 37px;
    border-bottom: 1px solid ${theme.palette.primary.main};
    background-color: ${theme.palette.background.paper};

    &:hover {
        background-color: ${theme.palette.border.light};
    }

    &.Mui-selected {
        background-color: ${theme.palette.background.card};
        color: ${theme.palette.primary.main};
        box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
        border: 1px solid ${theme.palette.primary.main};
        border-bottom-color: ${theme.palette.background.card};
    }
`;

const tabLabelStyle = css`
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
`;

const tabLabelNameStyle = css`
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
`;

const unSavedIndicatorStyle = (theme) => css`
    height: 8px;
    width: 8px;
    flex-shrink: 0;
    border-radius: 50%;
    margin-left: 12px;
    background: ${theme.palette.error.main};
`;

const closeButtonStyle = (theme) => css`
    padding: 2px;
    margin-left: 8px;
    margin-right: -4px;
    color: ${theme.palette.text.secondary};

    &:hover {
        background-color: ${theme.palette.action.hover};
    }

    .MuiSvgIcon-root {
        font-size: 16px;
    }
`;

const newTabStyle = (theme) => css`
    padding: 8px;
    display: flex;
    align-items: center;
    justify-content: flex-start;
    height: 100%;

    .bhi-add-thin {
        cursor: pointer;
        color: ${theme.palette.text.main};
        opacity: 0.6;

        &:hover {
            opacity: 1;
        }
    }
`;

// We use a -2px offset for the 100% to cater for the spacing
// that mui tabs adds, this lets our spacer line up nicely
// even when we have wrapped tabs
const spacerTabStyle = (theme) => css`
    flex-grow: 1;
    margin-left: -2px;
    height: calc(100% - 2px);
    align-items: center;
    position: relative;
    top: -1px;
    border-bottom: 1px solid ${theme.palette.primary.main};
`;

const KpiDrawerMetricTabs = (props) => {
  const {
    entityTypeToLabel,
    idToTemplate,
    idToWrappedKpi,
    openedKpiIds,
    focusedKpiTabId,
    onChangeMetricTab,
    onCloseMetricTab,
    onOpenMetricTab,
    onAddMetricClick
  } = props;

  const [showMetricSearch, setShowMetricSearch] = React.useState(false);
  const {theme} = React.useContext(CustomThemeContext);

  const selectedValue = focusedKpiTabId ? focusedKpiTabId : false;

  const handleChange = (event, kpiId) => {
    onChangeMetricTab(kpiId);
  };

  const handleCloseTab = (event, kpiId) => {
    event.stopPropagation(); // Prevent tab selection when clicking close
    onCloseMetricTab(kpiId);
  };

  const PICKER_WIDTH = 400;

  return (
      <div style={{display: "flex", height: 42, overflow: "hidden"}}>
        <Tabs
            onChange={handleChange}
            value={selectedValue}
            variant="scrollable"
            scrollButtons
            css={tabsStyle(theme)}
        >
          {openedKpiIds?.map((openKpiId) => {
            const wk = idToWrappedKpi.get(openKpiId);
            const kpi = wk.get("kpi");
            const kpiId = kpi.get("id");
            const kpiName = kpi.get("name");
            const hasUnsavedChanges = wk.get("changed") || wk.get("columnsChanged");

            return (
                <Tab
                    key={kpiId}
                    value={kpiId}
                    css={tabStyle(theme)}
                    wrapped={false}
                    label={
                      <Box css={tabLabelStyle}>
                        <span title={kpiName} css={tabLabelNameStyle}>{kpiName}</span>
                        {hasUnsavedChanges && <div css={unSavedIndicatorStyle(theme)} />}
                        <IconButton
                            size="small"
                            css={closeButtonStyle(theme)}
                            onClick={(e) => handleCloseTab(e, kpiId)}
                        >
                          <CloseIcon data-test-id="tab-close-icon" />
                        </IconButton>
                      </Box>
                    }
                />
            );
          })}
        </Tabs>
        <div css={spacerTabStyle(theme)}>
          {showMetricSearch ?
              <div style={{minWidth: PICKER_WIDTH, width: PICKER_WIDTH, position: "relative"}} data-test-id="metric-drawer-kpi-search">
                <KpiByEntityPicker
                    width={PICKER_WIDTH}
                    wrappedKpis={idToWrappedKpi.valueSeq()}
                    idToTemplate={idToTemplate}
                    entityTypeToLabel={entityTypeToLabel}
                    onKpiSelect={(kpiId) => onOpenMetricTab(kpiId)}
                    placeholder="Search for a metric..."
                    onAddMetricClick={onAddMetricClick}
                    menuIsOpen={showMetricSearch}
                    onMenuClose={() => setShowMetricSearch(false)}
                />
                <i
                    className="bhi-x" onClick={() => setShowMetricSearch(false)}
                    style={{cursor: "pointer", position: "absolute", top: 4, right: -14}}
                />
              </div>
              :
              <div
                  css={newTabStyle}
                  onClick={() => setShowMetricSearch(true)}>
                <i className="bhi-add-thin" />
              </div>
          }
        </div>
      </div>
  );
};

export default KpiDrawerMetricTabs;